import React, { Fragment } from 'react';
import { Form } from '../../ui/universe';
import { useMenuStore, useImageStore, useUIStore, useOcrStore } from '../../../store';
import { DeepSeekingCom, ResultingCom, EmptyCom } from './components';
import {
  MarkdownRenderer,
  OpenResultRenderer,
  ImageRenderer,
  ListRenderer,
  MathRenderer,
  EduAgentRenderer,
  ChildMapRenderer,
  WarnMapRenderer,
  MapRenderer,
  KeyMapRenderer,
  TableRenderer,
  SmartListRenderer,
  InvoiceRenderer,
  SealRenderer,
  WarnRenderer,
  RiskRenderer,
  QrcodeRenderer,
  StoreRenderer,
  EduRenderer,
  EduMarkRenderer,
  EssayEduRenderer,
  RecognizeAgentRenderer
} from './renderers';

/**
 * 渲染器映射表
 */
const rendererMap = {
  markdown: MarkdownRenderer,
  OpenResult: OpenResultRenderer,
  Image: ImageRenderer,
  list: ListRenderer,
  math: MathRenderer,
  eduAgent: EduAgentRenderer,
  childMap: ChildMapRenderer,
  warnMap: WarnMapRenderer,
  map: MapRenderer,
  keyMap: KeyMapRenderer,
  table: TableRenderer,
  smartList: SmartListRenderer,
  invoice: InvoiceRenderer,
  SealOCR: SealRenderer,
  warn: WarnRenderer,
  risk: RiskRenderer,
  QrcodeOCR: QrcodeRenderer,
  store: StoreRenderer,
  edu: EduRenderer,
  markEdu: EduMarkRenderer,
  essayEdu: EssayEduRenderer,
  recognizeAgent: RecognizeAgentRenderer
};

/**
 * 根据类型渲染对应的内容
 */
const renderByType = (type, props) => {
  const { params, config, detail, selectRect, onSelect, isLoading, request, showDownload } = props;

  switch (type) {
    case 'markdown':
      return <MarkdownRenderer params={params} config={config} request={request} />;

    case 'OpenResult':
      return <OpenResultRenderer params={params} config={config} isLoading={isLoading} />;

    case 'Image':
      return <ImageRenderer params={params} config={config} />;

    case 'list':
      return <ListRenderer params={params} config={config} detail={detail} selectRect={selectRect} onSelect={onSelect} />;

    case 'math':
      return <MathRenderer params={params} config={config} detail={detail} selectRect={selectRect} onSelect={onSelect} />;

    case 'eduAgent':
      return <EduAgentRenderer params={params} config={config} isLoading={isLoading} />;

    case 'childMap':
      return <ChildMapRenderer params={params} config={config} />;

    case 'warnMap':
      return <WarnMapRenderer params={params} config={config} />;

    case 'map':
      return <MapRenderer params={params} config={config} />;

    case 'keyMap':
      return <KeyMapRenderer params={params} config={config} detail={detail} selectRect={selectRect} onSelect={onSelect} />;

    case 'table':
      return <TableRenderer params={params} showDownload={showDownload} />;

    case 'smartList':
      return <SmartListRenderer params={params} detail={detail} selectRect={selectRect} onSelect={onSelect} request={request} />;

    case 'invoice':
      return <InvoiceRenderer params={params} config={config} detail={detail} selectRect={selectRect} onSelect={onSelect} />;

    case 'SealOCR':
      return <SealRenderer params={params} config={config} detail={detail} selectRect={selectRect} onSelect={onSelect} />;

    case 'warn':
      return <WarnRenderer params={params} config={config} selectRect={selectRect} onSelect={onSelect} />;

    case 'risk':
      return <RiskRenderer params={params} config={config} selectRect={selectRect} onSelect={onSelect} />;

    case 'QrcodeOCR':
      return <QrcodeRenderer params={params} config={config} detail={detail} selectRect={selectRect} onSelect={onSelect} />;

    case 'store':
      return <StoreRenderer params={params} detail={detail} selectRect={selectRect} onSelect={onSelect} />;

    case 'edu':
      return !isLoading && (
        <EduRenderer
          selectRect={selectRect}
          questionInfo={params.QuestionInfo}
          onSelect={(i) => onSelect && onSelect(i, i)}
        />
      );

    case 'markEdu':
      // 使用 JobId 作为 key，当数据变化时强制重新创建组件，避免 MathJax 排版错误
      const markEduKey = props.eduMarkData?.JobId || props.eduMarkData?.data?.Response?.JobId || 'default';
      return !isLoading && (
        <EduMarkRenderer key={markEduKey} onSelect={(i) => onSelect && onSelect(i, i)} />
      );

    case 'essayEdu':
      return !isLoading && (
        <EssayEduRenderer
          selectRect={selectRect}
          essayData={params?.SentenceSuggests || []}
          onSelect={(i) => onSelect && onSelect(i, i)}
        />
      );

    case 'recognizeAgent':
      return <RecognizeAgentRenderer params={params} config={config} isLoading={isLoading} />;

    default:
      return null;
  }
};

/**
 * OutputParams 组件 - OCR 识别结果展示
 */
function OutputParams({ resultType, waitingType = 'resulting', onSelect }) {
  // 从 store 获取状态
  const detail = useMenuStore(state => state.currentMenu);
  const image = useImageStore(state => state.currentImage);
  const selectRect = useImageStore(state => state.selectRectKey);
  const isLoading = useUIStore(state => state.isLoading);
  const isThinking = useUIStore(state => state.isThinking);
  const isMarkLoading = useUIStore(state => state.isMarkLoading);
  const request = useOcrStore(state => state.request);
  const params = useOcrStore(state => state.response);
  const eduMarkData = useOcrStore(state => state.eduMarkData);

  // 从 detail 获取配置
  const resultConfig = detail?.resultConfig;
  const showDownload = detail?.resultConfig?.showDownBtn && !image?.isExample;
  
  // 判断是否需要显示轮询期间的 loading
  // markEdu: 由 EduMarkRenderer 内部处理 loading，不在此层显示
  // Async 类型（如 essayEdu）: 使用 isThinking 状态
  const isAsyncActionType = detail?.actionType === 'Async';
  
  // 计算是否显示轮询 loading（markEdu 不在此层显示，由组件内部处理）
  const showPollingLoading = isAsyncActionType && isThinking;

  /**
   * 处理参数并渲染
   */
  const dealParams = (params, config, type) => {
    // markEdu 类型不依赖 params，而是从 store 中读取 eduMarkData，跳过空检查
    if (type !== 'markEdu' && JSON.stringify(params) === '{}') {
      return <EmptyCom />;
    }

    const renderProps = {
      params,
      config,
      detail,
      selectRect,
      onSelect,
      isLoading,
      request,
      showDownload,
      eduMarkData
    };

    const content = renderByType(type, renderProps);
    return content || <EmptyCom />;
  };

  /**
   * 处理并渲染结果
   */
  const handleDealParams = (params, resultType) => {
    if (JSON.stringify(params) === '{}') {
      return <EmptyCom />;
    }

    // 错误处理
    if (params.error) {
      return (
        <div className="result_error_content">
          <span className="ocrDemo-icon-error"></span>
          <span className="result_error_content_text"> {params.message}</span>
        </div>
      );
    }

    // 多配置数组
    if (Array.isArray(resultConfig)) {
      return (
        <>
          {resultConfig.map((item, index) => (
            <Fragment key={`config-${index}`}>
              {dealParams(params, item, resultType === 'OpenResult' ? 'OpenResult' : item.type)}
            </Fragment>
          ))}
        </>
      );
    }

    // 单配置
    const type = resultType === 'OpenResult' ? 'OpenResult' : resultConfig.type;
    return dealParams(params, resultConfig, type);
  };

  return (
    <Form layout="horizontal" split>
      {resultType === 'OpenResult' ? (
        handleDealParams(params, resultType)
      ) : (
        <>
          {isLoading ? (
            // 第一阶段：提交请求期间
            waitingType === 'OpenResult' ? <DeepSeekingCom /> : <ResultingCom msg={detail?.loadingText} />
          ) : showPollingLoading ? (
            // 第二阶段：轮询期间（markEdu 用 isMarkLoading，Async 用 isThinking）
            <ResultingCom msg={detail?.loadingText} />
          ) : (
            handleDealParams(params, resultType)
          )}
        </>
      )}
    </Form>
  );
}

export default OutputParams;
